import React, { useEffect, useState } from 'react';
import { Stack, Typography } from '@mui/material';
import { IAuditLogItemResponse } from 'models/auditLog';
import { IRegisteredFirearmData } from 'models/registeredFirearm';
import { RegisteredFirearmDetails } from 'components/organisms/RegisteredFirearmDetails';
import { AuditLogEventName } from 'pages/AuditLogPage/AuditLogViewPage/styles';
import { DetailsTemplate, EDetailsPageTypes } from 'components/templates/DetailsTemplate';
import { useTranslations } from 'hooks/useTranslations';
import { EAuditLogEvents } from 'pages/AuditLogPage/helpers';
import { IFRTDetails, IFRTSpecifications } from 'models/frt';
import { Box } from '@mui/system';
import { StyledDetailsWrapper } from 'pages/Firearms/RegisteredFirearmDeleteCommentPage/styles';
import Grid from '@mui/material/Grid';
import { isFRTLocal } from 'pages/FRT/helpers';
import { ERouteLinks } from 'models/route';
import { useNavigate } from 'react-router-dom';
import { findInDictionary } from 'helpers/dictionary';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { useFirearms } from 'pages/Firearms/hooks';
import { EDictionaryTypes } from 'models/dictionary';
import { useLang } from 'models/langContext';

interface IFirearmEventDetails {
  auditLog: IAuditLogItemResponse;
}

const FirearmEventDetails = ({ auditLog }: IFirearmEventDetails) => {
  const [firearm, setFirearm] = useState<IRegisteredFirearmData | null>();
  const [previousFirearm, setPreviousFirearm] = useState<IRegisteredFirearmData | null>();
  const [previousFrt, setPreviousFrt] = useState<IFRTDetails | null>();
  const [previousFrtSpecifications, setPreviousFrtSpecifications] = useState<
    IFRTSpecifications[] | null
  >();
  const { t } = useTranslations();
  const [FRTDetails, setFRTDetails] = useState<IFRTDetails | null>(null);
  const [FRTSpecifications, setFRTSpecifications] = useState<IFRTSpecifications[]>([]);
  const navigate = useNavigate();
  const {
    loadDictionaries,
    state,
    make,
    model,
    firearmAction,
    caliber,
    manufacturer,
    country,
  } = useFirearms();
  const { selectedLanguage } = useLang();

  useEffect(() => {
    (async function init() {
      await loadDictionaries([
        EDictionaryTypes.State,
        EDictionaryTypes.Make,
        EDictionaryTypes.Model,
        EDictionaryTypes.FirearmAction,
        EDictionaryTypes.Caliber,
        EDictionaryTypes.Manufacturer,
        EDictionaryTypes.Country,
      ]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    if (
      auditLog.eventNameUniqueId === EAuditLogEvents.ADD_FIREARM_TO_FRT_LOCAL ||
      auditLog.eventNameUniqueId === EAuditLogEvents.EDIT_FIREARM_FROM_FRT_LOCAL
    ) {
      if (auditLog?.previousObjectValue) {
        (async () => {
          const previousObjectValue = auditLog.previousObjectValue as any;
          const previousFrtObjectValue = previousObjectValue.frt as any;
          const previousSpecificationsObjectValue = previousObjectValue.specifications as any;
          const previousFrtData: IFRTDetails = {
            actionUniqueId: previousFrtObjectValue.actionUniqueId || '',
            countryUniqueId: previousFrtObjectValue.countryUniqueId || '',
            frtId: previousFrtObjectValue.frtId || '',
            id: previousFrtObjectValue.uniqueId || '',
            isLocal: previousFrtObjectValue.isLocal || '',
            legalClassification: previousFrtObjectValue.legalClassification || '',
            level: previousFrtObjectValue.level || '',
            makeUniqueId: previousFrtObjectValue.makeUniqueId || '',
            manufacturerUniqueId: previousFrtObjectValue.manufacturerUniqueId || '',
            modelUniqueId: previousFrtObjectValue.modelUniqueId || '',
            productCode: previousFrtObjectValue.productCode || '',
            productCodes: previousFrtObjectValue.productCodes || [],
            typeUniqueId: previousFrtObjectValue.typeUniqueId || '',
          };
          setPreviousFrtSpecifications(previousSpecificationsObjectValue);
          setPreviousFrt(previousFrtData);
        })();
      }
      (async () => {
        if (auditLog?.postObjectValue) {
          const postObjectValue = auditLog?.postObjectValue as any;
          postObjectValue.frt.id = postObjectValue.frt.uniqueId
            ? postObjectValue.frt.uniqueId
            : postObjectValue.frt.id;
          setFRTDetails(postObjectValue?.frt);
          setFRTSpecifications(postObjectValue?.specifications);
        }
      })();
    } else {
      if (auditLog?.previousObjectValue) {
        (async () => {
          const previousObjectValue = auditLog?.previousObjectValue as any;
          const previousFirearmData: IRegisteredFirearmData = {
            keeperVisibilityUniqueId: '',
            ownerVisibilityUniqueId: '',
            showInactiveOwnerOrKeeperWarning:
              previousObjectValue.showInactiveOwnerOrKeeperWarning || false,
            productCodes: previousObjectValue.productCodes || [],
            actionUniqueId: previousObjectValue.actionUniqueId || '',
            activityUniqueId: previousObjectValue.activityUniqueId || '',
            caliberUniqueId: previousObjectValue.caliberUniqueId || '',
            countryOfExport: previousObjectValue.countryOfExport || '',
            countryOfImport: previousObjectValue.countryOfImport || '',
            customFields: previousObjectValue.customFields || [],
            exhibitNumber: previousObjectValue.exhibitNumber || '',
            externalLocalReferenceNumber:
              previousObjectValue.externalLocalReferenceNumber || '',
            appliedOrImportMarking: previousObjectValue.appliedOrImportMarking || '',
            fireCycleId: previousObjectValue.fireCycleId || '',
            frtId: previousObjectValue.frtId || '',
            frtUniqueId: previousObjectValue.frtUniqueId || '',
            groupName: previousObjectValue.groupName || '',
            importer: previousObjectValue.importer || '',
            keeper: previousObjectValue.keeper || '',
            keeperId: previousObjectValue.keeperId || '',
            keeperType: previousObjectValue.keeperType || '',
            latitude: previousObjectValue.latitude || '',
            legality: previousObjectValue.legality || '',
            legalityUniqueId: previousObjectValue.legalityUniqueId || '',
            longitude: previousObjectValue.longitude || '',
            makeUniqueId: previousObjectValue.makeUniqueId || '',
            manufacturedAt: previousObjectValue.manufacturedAt || '',
            manufacturerUniqueId: previousObjectValue.manufacturerUniqueId || '',
            modelUniqueId: previousObjectValue.modelUniqueId || '',
            owner: previousObjectValue.owner || '',
            ownerId: previousObjectValue.ownerId || '',
            ownerType: previousObjectValue.ownerType || '',
            serialNumber: previousObjectValue.serialNumber || '',
            state: previousObjectValue.state || '',
            stateUniqueId: previousObjectValue.stateUniqueId || '',
            typeUniqueId: previousObjectValue.typeUniqueId || '',
            version: previousObjectValue.version || '',
            isKeeperHyperLinkActive: previousObjectValue.isKeeperHyperLinkActive || '',
            isOwnerHyperLinkActive: previousObjectValue.isOwnerHyperLinkActive || '',
            uniqueId: '',
            licenceAuthorityIds: [],
            markings: previousObjectValue.markings || null,
            yearOfConfiscation: previousObjectValue.yearOfConfiscation || '',
            yearOfTransfer: previousObjectValue.yearOfTransfer || '',
            wasImported: previousObjectValue.wasImported || '',
          };
          setPreviousFirearm(previousFirearmData);
        })();
      }
      (async () => {
        if (auditLog?.postObjectValue) {
          const postObjectValue = auditLog.postObjectValue as any;
          postObjectValue.state = findInDictionary(postObjectValue.stateUniqueId, state);
          postObjectValue.owner = postObjectValue.ownerName || postObjectValue.owner;
          postObjectValue.keeper = postObjectValue.keeperName || postObjectValue.keeper;
          setFirearm(postObjectValue);
        }
      })();
    }
  }, [auditLog, state]);

  const isPreviousFrtLocal: boolean = isFRTLocal(previousFrt?.id || '');
  const isFrtLocal: boolean = isFRTLocal(FRTDetails?.id || '');

  let previousDetailsTitle = '';
  if (previousFrt !== null && previousFrt !== undefined) {
    const { makeUniqueId, modelUniqueId, actionUniqueId } = previousFrt;
    previousDetailsTitle = `${findInDictionary(makeUniqueId, make)}, ${findInDictionary(modelUniqueId, model)}, ${findInDictionary(actionUniqueId, firearmAction)}`;
  }

  let frtDetailsTitle = '';
  if (FRTDetails) {
    const { makeUniqueId, modelUniqueId, actionUniqueId } = FRTDetails;
    frtDetailsTitle = `${findInDictionary(makeUniqueId, make)}, ${findInDictionary(modelUniqueId, model)}, ${findInDictionary(actionUniqueId, firearmAction)}`;
  }

  const previousFrtCalibres = previousFrtSpecifications
    ? previousFrtSpecifications
        ?.map((spec) => findInDictionary(spec.caliberUniqueId, caliber))
        .join(', ') || '-'
    : '-';
  const frtCalibres = FRTSpecifications
    ? FRTSpecifications.map((spec) => findInDictionary(spec.caliberUniqueId, caliber)).join(
        ', ',
      ) || '-'
    : '-';

  return (
    <>
      {previousFrt && (
        <>
          <AuditLogEventName sx={{ marginTop: '20px' }}>
            <Typography variant="h5">
              {isPreviousFrtLocal
                ? `${t('firearmsReferenceTable.editFirearm.viewLocalFirearm.header')} ${t(
                    'auditLogDetails.before.event.title',
                  )}`
                : `${t('firearmsReferenceTable.editFirearm.viewFirearm.header')} ${t(
                    'auditLogDetails.before.event.title',
                  )}`}
            </Typography>
          </AuditLogEventName>
          <DetailsTemplate
            title={previousDetailsTitle}
            pageType={EDetailsPageTypes.auditLogView}
            hasLongTitle={true}
            disableActions
          >
            <Grid size={{ xs: 12 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('firearmsReferenceTable.editFirearm.calibres.label')}
                </Typography>
                <Typography variant="body2">{previousFrtCalibres}</Typography>
              </Stack>
            </Grid>
          </DetailsTemplate>
          <Box sx={{ mb: 2 }}>
            <StyledDetailsWrapper>
              <Grid size={{ xs: 12, lg: 2.4 }}>
                <Typography variant="subtitle2">
                  {isPreviousFrtLocal
                    ? t(
                        'firearmsReferenceTable.editFirearm.general.accordion.firearmReferenceLocalID.label',
                      )
                    : t(
                        'firearmsReferenceTable.editFirearm.general.accordion.firearmReferenceNo.label',
                      )}
                </Typography>
                <Typography variant="body2" sx={{ marginTop: '4px' }}>
                  {emptyFieldHolder(isPreviousFrtLocal ? previousFrt.frtId : previousFrt.id)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, lg: 2.4 }}>
                <Typography variant="subtitle2">
                  {t(
                    'firearmsReferenceTable.editFirearm.general.accordion.manufacturer.label',
                  )}
                </Typography>
                <Typography variant="body2" sx={{ marginTop: '4px' }}>
                  {findInDictionary(previousFrt.manufacturerUniqueId, manufacturer)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, lg: 2.4 }}>
                <Typography variant="subtitle2">
                  {t(
                    'firearmsReferenceTable.editFirearm.general.accordion.countryOfManufacturer.label',
                  )}
                </Typography>
                <Typography variant="body2" sx={{ marginTop: '4px' }}>
                  {findInDictionary(previousFrt.countryUniqueId, country)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, lg: 2.4 }}>
                <Typography variant="subtitle2">
                  {t('firearmsReferenceTable.editFirearm.general.accordion.productCode.label')}
                </Typography>
                <Typography variant="body2" sx={{ marginTop: '4px' }}>
                  {previousFrt.productCodes.length > 0
                    ? previousFrt.productCodes.join(', ')
                    : '-'}
                </Typography>
              </Grid>
            </StyledDetailsWrapper>
          </Box>
        </>
      )}

      {FRTDetails && (
        <>
          <AuditLogEventName sx={{ marginTop: '20px' }}>
            <Typography variant="h5">
              {isFrtLocal
                ? `${t('firearmsReferenceTable.editFirearm.viewLocalFirearm.header')} ${t(
                    'auditLogDetails.post.event.title',
                  )}`
                : `${t('firearmsReferenceTable.editFirearm.viewFirearm.header')} ${t(
                    'auditLogDetails.post.event.title',
                  )}`}
            </Typography>
          </AuditLogEventName>
          <DetailsTemplate
            title={frtDetailsTitle}
            pageType={EDetailsPageTypes.auditLogView}
            hasLongTitle={true}
            redirectToView={
              isFrtLocal
                ? () => navigate(ERouteLinks.FRTDetails.replace(':frtId', FRTDetails.id))
                : undefined
            }
          >
            <Grid size={{ xs: 12 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('firearmsReferenceTable.editFirearm.calibres.label')}
                </Typography>
                <Typography variant="body2">{frtCalibres}</Typography>
              </Stack>
            </Grid>
          </DetailsTemplate>
          <Box sx={{ mb: 2 }}>
            <StyledDetailsWrapper>
              <Grid size={{ xs: 12, lg: 2.4 }}>
                <Typography variant="subtitle2">
                  {isFrtLocal
                    ? t(
                        'firearmsReferenceTable.editFirearm.general.accordion.firearmReferenceLocalID.label',
                      )
                    : t(
                        'firearmsReferenceTable.editFirearm.general.accordion.firearmReferenceNo.label',
                      )}
                </Typography>
                <Typography variant="body2" sx={{ marginTop: '4px' }}>
                  {emptyFieldHolder(isFrtLocal ? FRTDetails.frtId : FRTDetails.id)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, lg: 2.4 }}>
                <Typography variant="subtitle2">
                  {t(
                    'firearmsReferenceTable.editFirearm.general.accordion.manufacturer.label',
                  )}
                </Typography>
                <Typography variant="body2" sx={{ marginTop: '4px' }}>
                  {findInDictionary(FRTDetails.manufacturerUniqueId, manufacturer)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, lg: 2.4 }}>
                <Typography variant="subtitle2">
                  {t(
                    'firearmsReferenceTable.editFirearm.general.accordion.countryOfManufacturer.label',
                  )}
                </Typography>
                <Typography variant="body2" sx={{ marginTop: '4px' }}>
                  {findInDictionary(FRTDetails.countryUniqueId, country)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, lg: 2.4 }}>
                <Typography variant="subtitle2">
                  {t('firearmsReferenceTable.editFirearm.general.accordion.productCode.label')}
                </Typography>
                <Typography variant="body2" sx={{ marginTop: '4px' }}>
                  {FRTDetails.productCodes.length > 0
                    ? FRTDetails.productCodes.join(', ')
                    : '-'}
                </Typography>
              </Grid>
            </StyledDetailsWrapper>
          </Box>
        </>
      )}

      {previousFirearm && auditLog?.eventNameUniqueId !== EAuditLogEvents.ADD_FIREARM && (
        <>
          <AuditLogEventName>
            <Typography variant="h5">
              {`${t('auditLogDetails.firearmsDetails.title')} ${t(
                'auditLogDetails.before.event.title',
              )}`}
            </Typography>
          </AuditLogEventName>
          <RegisteredFirearmDetails
            firearm={previousFirearm}
            pageType={EDetailsPageTypes.auditLogView}
            disableActions={!previousFirearm.uniqueId}
          />
        </>
      )}

      {firearm && (
        <>
          <AuditLogEventName sx={{ marginTop: '20px' }}>
            <Typography variant="h5">
              {`${t('auditLogDetails.firearmsDetails.title')} ${t(
                'auditLogDetails.post.event.title',
              )}`}
            </Typography>
          </AuditLogEventName>
          <RegisteredFirearmDetails
            firearm={firearm}
            pageType={EDetailsPageTypes.auditLogView}
            disableActions={!firearm.uniqueId}
          />
        </>
      )}
    </>
  );
};

export default FirearmEventDetails;
